import { Fragment, useEffect, useRef, useState } from 'react';
import { useContent, useSite } from '@/services/siteData';
import { useIsomorphicLayoutEffect } from '@/utils/useIsomorphicLayoutEffect';
import { formatIngredients } from '@/utils/ingredients';
import { EnquiryForm } from '@/components/EnquiryForm';
import { Seo } from '@/components/Seo';
import type { Product } from '@/data/catalogue';

export function InnovationPage() {
  const { home, innovation } = useContent();
  const { getInnovationProducts, deriveFormat } = useSite();
  const { promos } = home;
  const products = getInnovationProducts();

  const [selected, setSelected] = useState<string | null>(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [cols, setCols] = useState(4);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardEls = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRef = useRef<HTMLDivElement>(null);

  // Number of grid columns at the current breakpoint (read from the CSS grid).
  useEffect(() => {
    const measure = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const tracks = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      setCols(Math.max(1, tracks));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [products.length]);

  const selIndex = selected ? products.findIndex((p) => p.code === selected) : -1;
  // Insert the expander after the last card in the selected card's row.
  const rowEnd =
    selIndex >= 0 ? Math.min(Math.floor(selIndex / cols) * cols + cols - 1, products.length - 1) : -1;

  // Point the panel's triangle at the selected card and scroll it into view.
  useIsomorphicLayoutEffect(() => {
    if (selIndex < 0) return;
    const card = cardEls.current[selected as string];
    const panel = panelRef.current;
    if (!card || !panel) return;
    const cardRect = card.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    panel.style.setProperty('--arrow-left', `${cardRect.left + cardRect.width / 2 - panelRect.left}px`);
  }, [selIndex, selected, cols]);

  useEffect(() => {
    if (selIndex < 0) return;
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selIndex]);

  const renderExpander = (product: Product) => {
    const weight = product.format ?? deriveFormat(product.name);
    const suitability = product.suitability?.join(', ');
    const allergens = product.allergens?.join(', ');
    const productContext = product.name + (product.description ? `\n${product.description}` : '');

    return (
      <div className="inno-expander" ref={panelRef}>
        <div className="inno-expander-inner">
          <button
            className="inno-close"
            aria-label="Close"
            onClick={() => {
              setSelected(null);
              setEnquiryOpen(false);
            }}
          />
          <div className="inno-detail">
            <div className="inno-detail-img">
              <img src={product.image} alt={product.name} />
            </div>
            <div className="inno-detail-body">
              <h3>{product.name}</h3>
              {product.description && <p className="inno-desc">{product.description}</p>}
              {suitability && (
                <p>
                  <span>Suitable for:</span> {suitability}
                </p>
              )}
              {weight && (
                <p>
                  <span>Weight:</span> {weight}
                </p>
              )}
              {product.ingredients && (
                <p className="inno-ingredients">
                  <span>Ingredients:</span>{' '}
                  <span dangerouslySetInnerHTML={{ __html: formatIngredients(product.ingredients) }} />
                </p>
              )}
              {allergens && (
                <p className="inno-allergens">
                  <span>Allergens:</span> {allergens}
                </p>
              )}
              {enquiryOpen ? (
                <div className="inno-enquiry">
                  <EnquiryForm type="innovation" product={productContext} withMessage />
                </div>
              ) : (
                <p className="inno-tel">
                  If you require bespoke patisserie please get in touch today to discuss.{' '}
                  <button type="button" className="inno-enquiry-btn" onClick={() => setEnquiryOpen(true)}>
                    Make an enquiry
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-fade">
      <Seo
        title="Bespoke Patisserie Development — Trade & Food Service"
        description="Bespoke and own-label patisserie development for food service and wholesale clients. Work with our chefs to create signature desserts for your menu."
        path="/innovation"
      />
      <div className="page-hero" style={{ backgroundImage: `url(${promos[0]?.img})` }} />
      <div className="page-head">
        <div className="eyebrow">{innovation.eyebrow}</div>
        <h1>{innovation.title}</h1>
        <p className="lede">{innovation.lede}</p>
        <p className="sub">{innovation.sub}</p>
      </div>
      <div className="story">
        {innovation.body.map((t, i) => (
          <p key={i}>{t}</p>
        ))}
        <div className="story-img" style={{ backgroundImage: `url(${promos[3]?.img})` }} />
      </div>

      {products.length > 0 && (
        <div className="grid inno-grid" ref={gridRef}>
          {products.map((p, i) => (
            <Fragment key={p.code}>
              <button
                type="button"
                ref={(el) => {
                  cardEls.current[p.code] = el;
                }}
                className={'card inno-card' + (p.code === selected ? ' active' : '')}
                onClick={() => {
                  setEnquiryOpen(false);
                  setSelected((cur) => (cur === p.code ? null : p.code));
                }}
              >
                <div className="card-img">
                  <img src={p.image} alt={p.name} loading="lazy" />
                </div>
                <div className="card-name">{p.name}</div>
              </button>
              {i === rowEnd && selIndex >= 0 && renderExpander(products[selIndex])}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
